import {
  Flex,
  HStack,
  Icon,
  Text,
  Box,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuDivider,
  Portal,
} from '@chakra-ui/react';
import { FaRegClock, FaEllipsisV } from 'react-icons/fa';
import { RiStackLine, RiTerminalBoxLine, RiRobot2Line } from 'react-icons/ri';
import { MdOutlineSmartToy } from 'react-icons/md';
import { IoTrashOutline, IoSettingsOutline, IoRefreshOutline } from 'react-icons/io5';
import { DtType, ProjectType, UpdateFrequency } from '../../generated/graphql';
import { getIndustryDetails } from '../../utils/industryDetails';
import { formatDateShort, formatTimeRemaining } from '../../utils/formatDate';
import { IoTimerOutline } from 'react-icons/io5';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanLabel } from '../helivan/HelivanLabel';

interface ProjectCardProps {
  id: string;
  title: string;
  subtopics: string[];
  dateCreated: string;
  industry: DtType;
  onClick?: () => void;
  hasCompleted?: boolean;
  hasFailed?: boolean;
  explorableCount?: number;
  agentCount?: number;
  onDelete?: (id: string) => void;
  onSettings?: (id: string) => void;
  onUpdate?: (id: string) => void;
  onRetry?: (id: string) => void;
  errorMessage?: string | null;
  nextScheduledUpdate?: string | null;
  updateFrequency?: UpdateFrequency | null;
  autoUpdateEnabled?: boolean | null;
  /** Watch type + a representative agent name, for an informative card header. */
  watchType?: ProjectType | null;
  agentName?: string | null;
  /** When true, marks this card as the guided-tour "open a watch" anchor. */
  tourAnchor?: boolean;
}

export const ProjectCard = ({
  id,
  title,
  subtopics,
  industry,
  dateCreated,
  onClick,
  hasCompleted = true,
  hasFailed = false,
  explorableCount,
  agentCount,
  onDelete,
  onSettings,
  onUpdate,
  onRetry,
  errorMessage,
  nextScheduledUpdate,
  updateFrequency,
  autoUpdateEnabled,
  watchType,
  agentName,
  tourAnchor = false,
}: ProjectCardProps) => {
  const { industryDetails } = getIndustryDetails(industry);
  const isClickable = hasCompleted && onClick;
  // Distinct headers per watch type:
  //  - Coding agent (planning) → terminal icon + agent / "Coding agent"
  //  - Personal agent (your own CustomAgent, non-coding) → robot icon + agent name
  //  - Persona (platform-curated DigitalTwin, no agentName) → industry icon + title
  // agentName is the latest version's CustomAgent name (null for persona watches).
  const isCodingWatch = watchType === ProjectType.PlanningProbe;
  const isPersonalAgentWatch = !isCodingWatch && !!agentName;
  const headerIcon = isCodingWatch
    ? RiTerminalBoxLine
    : isPersonalAgentWatch
      ? RiRobot2Line
      : industryDetails.icon;
  const headerLabel = isCodingWatch
    ? agentName || 'Coding agent'
    : isPersonalAgentWatch
      ? agentName
      : industryDetails.title;

  return (
    <Flex
      {...(tourAnchor ? { 'data-tour': 'watch-card' } : {})}
      width="full"
      height="100%"
      minWidth="240px"
      maxWidth="300px"
      flexDirection="column"
      bg={hasCompleted ? 'white' : HelivanColor.background.secondary}
      borderRadius="12px"
      overflow="hidden"
      boxShadow="0 1px 3px rgba(0,0,0,0.06)"
      border="1px solid"
      borderColor={HelivanColor.border.primary}
      _hover={
        isClickable
          ? {
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(0,71,125,0.1)',
              borderColor: HelivanColor.text.helivanBlue,
            }
          : undefined
      }
      transition="all 0.15s ease"
      cursor={isClickable ? 'pointer' : 'default'}
      onClick={onClick}
    >
      {/* Compact header with industry icon */}
      <Flex
        px="14px"
        py="10px"
        bg={HelivanColor.background.secondary}
        alignItems="center"
        justifyContent="space-between"
        borderBottom="1px solid"
        borderColor={HelivanColor.border.primary}
      >
        <HStack spacing={2}>
          <Icon
            as={headerIcon}
            width="14px"
            height="14px"
            color={HelivanColor.text.helivanBlue}
          />
          <Text fontSize="11px" fontWeight={500} color={HelivanColor.text.blueMid} noOfLines={1} maxWidth="160px">
            {headerLabel}
          </Text>
        </HStack>
        <HStack spacing={2}>
          <HStack spacing={1}>
            <FaRegClock size={10} color={HelivanColor.text.blueMid} />
            <Text fontSize="10px" color={HelivanColor.text.blueMid}>
              {formatDateShort(dateCreated)}
            </Text>
          </HStack>
          {(onUpdate || onSettings || onDelete) && (
            <Box onClick={(e) => e.stopPropagation()}>
              <Menu isLazy strategy="fixed" placement="bottom-end">
                <MenuButton
                  as={IconButton}
                  aria-label="Project options"
                  icon={<FaEllipsisV size={10} />}
                  size="xs"
                  variant="ghost"
                  minW="24px"
                  h="24px"
                  borderRadius="4px"
                  color={HelivanColor.text.blueMid}
                  _hover={{ bg: HelivanColor.background.secondary }}
                />
                <Portal>
                  <MenuList
                    fontSize="13px"
                    zIndex={1400}
                    boxShadow="0 4px 20px rgba(0,0,0,0.15)"
                    borderRadius="8px"
                    border="1px solid"
                    borderColor={HelivanColor.border.primary}
                    py={2}
                    minW="160px"
                  >
                    {onUpdate && (
                      <MenuItem
                        icon={<IoRefreshOutline size={14} />}
                        onClick={() => onUpdate(id)}
                        py={2}
                        px={3}
                        _hover={{ bg: HelivanColor.background.secondary }}
                      >
                        Update Now
                      </MenuItem>
                    )}
                    {onSettings && (
                      <MenuItem
                        icon={<IoSettingsOutline size={14} />}
                        onClick={() => onSettings(id)}
                        py={2}
                        px={3}
                        _hover={{ bg: HelivanColor.background.secondary }}
                      >
                        Settings
                      </MenuItem>
                    )}
                    {onDelete && (onUpdate || onSettings) && <MenuDivider />}
                    {onDelete && (
                      <MenuItem
                        icon={<IoTrashOutline size={14} />}
                        onClick={() => onDelete(id)}
                        py={2}
                        px={3}
                        color="red.500"
                        _hover={{ bg: 'red.50' }}
                      >
                        Delete
                      </MenuItem>
                    )}
                  </MenuList>
                </Portal>
              </Menu>
            </Box>
          )}
        </HStack>
      </Flex>

      <Flex flexDirection="column" p="14px" gap="10px" flex="1">
        {/* Title row with explorable count */}
        <Flex width="full" justifyContent="space-between" alignItems="flex-start" gap={2}>
          <HStack spacing={2} flex="1" alignItems="flex-start">
            <Text
              fontSize="15px"
              fontWeight={600}
              color={HelivanColor.text.blueDark}
              lineHeight="1.3"
              noOfLines={2}
              flex="1"
            >
              {title}
            </Text>
            {agentCount !== undefined && agentCount > 0 && (
              <HStack
                spacing={1}
                px="6px"
                py="2px"
                borderRadius="4px"
                bg={HelivanColor.background.secondary}
                flexShrink={0}
              >
                <MdOutlineSmartToy size={10} color={HelivanColor.text.blueMid} />
                <Text fontSize="10px" fontWeight={500} color={HelivanColor.text.blueMid}>
                  {agentCount}
                </Text>
              </HStack>
            )}
            {explorableCount !== undefined && explorableCount > 0 && (
              <HStack
                spacing={1}
                px="6px"
                py="2px"
                borderRadius="4px"
                bg={HelivanColor.text.helivanBlue}
                flexShrink={0}
              >
                <RiStackLine size={10} color="white" />
                <Text fontSize="10px" fontWeight={500} color="white">
                  {explorableCount}
                </Text>
              </HStack>
            )}
          </HStack>
          {hasFailed ? (
            <HelivanLabel label="Failed" variant="error" size="sm" />
          ) : !hasCompleted ? (
            <HelivanLabel label="Processing" variant="blueDark" size="sm" />
          ) : null}
        </Flex>

        {/* Subtopics - compact */}
        {subtopics.length > 0 && (
          <Flex gap="4px" flexWrap="wrap">
            {subtopics.slice(0, 2).map((label, idx) => (
              <HelivanLabel
                key={idx}
                label={label}
                variant={idx === 0 ? 'blue' : 'success'}
                size="sm"
              />
            ))}
            {subtopics.length > 2 && (
              <Box
                px="6px"
                py="2px"
                borderRadius="4px"
                bg={HelivanColor.background.secondary}
              >
                <Text fontSize="10px" color={HelivanColor.text.blueMid}>
                  +{subtopics.length - 2}
                </Text>
              </Box>
            )}
          </Flex>
        )}

        {/* Error message + retry for failed projects */}
        {hasFailed && (
          <Flex direction="column" gap="6px">
            {errorMessage && (
              <Text fontSize="11px" color="red.600" noOfLines={2}>
                {errorMessage}
              </Text>
            )}
            {onRetry && (
              <Box
                as="button"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onRetry(id);
                }}
                px="8px"
                py="3px"
                borderRadius="4px"
                border="1px solid"
                borderColor="red.200"
                bg="white"
                _hover={{ bg: 'red.50' }}
                width="fit-content"
                transition="all 0.15s"
              >
                <HStack spacing={1}>
                  <IoRefreshOutline size={11} color="#E53E3E" />
                  <Text fontSize="11px" fontWeight={500} color="red.500">
                    Retry
                  </Text>
                </HStack>
              </Box>
            )}
          </Flex>
        )}

        {/* Next scheduled update */}
        {autoUpdateEnabled && updateFrequency !== UpdateFrequency.Manual && nextScheduledUpdate ? (
          <HStack spacing={1}>
            <IoTimerOutline size={11} color={HelivanColor.text.blueMid} />
            <Text fontSize="10px" color={HelivanColor.text.blueMid}>
              Next {formatTimeRemaining(nextScheduledUpdate)}
            </Text>
          </HStack>
        ) : (!autoUpdateEnabled || updateFrequency === UpdateFrequency.Manual) && hasCompleted ? (
          <HStack spacing={1}>
            <IoTimerOutline size={11} color={HelivanColor.text.blueMid} />
            <Text fontSize="10px" color={HelivanColor.text.blueMid}>
              Next in ∞
            </Text>
          </HStack>
        ) : null}
      </Flex>
    </Flex>
  );
};
